import Phaser from 'phaser';
import type { Score } from '../../../shared/scoring.js';
import type { Rect } from '../core/playfield.js';
import { CSS, PALETTE, TYPE, UI_FONT, UI_HEAVY, formatEuro } from './style.js';

export type EndingStep = 'result' | 'story' | 'cta';

/** What the end of a round says – the edition brings the texts (client-shared/campaign.ts). */
export interface EndingContent {
  /** title and text of the result screen by result (the maximum gets its own) */
  result(score: Score): { title: string; text: string };
  /** one screen per part, they share the time from `storyAtSec` to `ctaAtSec` – none: the call to action follows the result */
  story: readonly string[];
  /** labels of the two amounts of the result: own contribution, subsidy */
  amounts: { own: string; subsidy: string };
  /** `color` of the headline, default orange */
  cta: { headline: string; hint?: string; color?: string };
  /** seconds after the end of the round */
  storyAtSec: number;
  ctaAtSec: number;
  /** share of the window kept free at the bottom (buttons of the edition) … */
  bottomSpace?: number;
  /** … and at the top (a painted HUD panel reaching into the window) */
  topSpace?: number;
}

const CONFETTI_KEY = 'end-confetti';
/** the game's colours – the same confetti as on the phone afterwards (controller) */
const CONFETTI_COLORS = [PALETTE.orange, PALETTE.yellow, 0x9fd0ff, PALETTE.white, PALETTE.blueLight];
/** a piece falls through the window in this many seconds (min, max) … */
const CONFETTI_FALL_SEC = [1.8, 3.4] as const;
/** … and starts within this time (s) */
const CONFETTI_SPREAD_SEC = 0.6;
/** the result counts up this long */
const COUNT_SEC = 1.3;

/**
 * End of a round, over the whole window (like "Spiel beginnt"): result with own contribution + subsidy
 * counting up and a text by result, with confetti → story (part by part) → call to action (continued on the phone). The
 * maximum gets a golden result and more confetti.
 */
export class EndScreen {
  private readonly root: Phaser.GameObjects.Container;
  private readonly card: Phaser.GameObjects.Container;
  private readonly title: Phaser.GameObjects.Text;
  /** the two amounts, each below its label */
  private readonly depotLabel: Phaser.GameObjects.Text;
  private readonly depot: Phaser.GameObjects.Text;
  private readonly subsidyLabel: Phaser.GameObjects.Text;
  private readonly subsidy: Phaser.GameObjects.Text;
  private readonly text: Phaser.GameObjects.Text;
  private readonly confetti: Phaser.GameObjects.Particles.ParticleEmitter;
  /** each piece's start angle and turns while it falls */
  private readonly spins = new WeakMap<Phaser.GameObjects.Particles.Particle, { from: number; turn: number }>();
  private content: EndingContent | null = null;
  private score: Score | null = null;
  private sec = 0;
  private _step: EndingStep | null = null;
  /** the part of the story on screen */
  private part = -1;
  private counted = -1;
  /** pixels per size unit of the texts */
  private readonly unit: number;

  constructor(
    private readonly scene: Phaser.Scene,
    /** pixels per TYPE unit (PlayScene.textUnit) */
    unit: number,
    width: number,
    height: number,
    /** the largest visible part of the window – the texts go there */
    private readonly area: Rect,
    depth: number,
    private readonly onStep: (step: EndingStep) => void,
  ) {
    // lines break between words
    const px = (u: number) => Math.max(1, Math.round(u * unit));
    const style = (size: number, color: string, weight: string, wrap = true): Phaser.Types.GameObjects.Text.TextStyle => ({
      fontFamily: UI_FONT,
      fontSize: `${px(size)}px`,
      fontStyle: weight,
      color,
      align: 'center',
      stroke: CSS.ink,
      strokeThickness: px(size * 0.12),
      lineSpacing: px(size * TYPE.lineSpacing),
      wordWrap: wrap ? { width: area.width * 0.9 } : undefined,
    });
    this.unit = unit;
    const bg = scene.add.rectangle(0, 0, width, height, PALETTE.ink, 0.9).setOrigin(0, 0);
    this.title = scene.add.text(0, 0, '', style(TYPE.title, CSS.orange, UI_HEAVY)).setOrigin(0.5, 0);
    this.depotLabel = scene.add.text(0, 0, '', style(TYPE.text, CSS.text, 'bold')).setOrigin(0.5, 0);
    this.depot = scene.add.text(0, 0, '', style(TYPE.title, CSS.text, UI_HEAVY, false)).setOrigin(0.5, 0);
    this.subsidyLabel = scene.add.text(0, 0, '', style(TYPE.text, CSS.text, 'bold')).setOrigin(0.5, 0);
    this.subsidy = scene.add.text(0, 0, '', style(TYPE.title, CSS.text, UI_HEAVY, false)).setOrigin(0.5, 0);
    this.text = scene.add.text(0, 0, '', style(TYPE.text, CSS.text, 'bold')).setOrigin(0.5, 0);
    this.card = scene.add.container(0, 0, [this.title, this.depotLabel, this.depot, this.subsidyLabel, this.subsidy, this.text]);
    this.root = scene.add.container(0, 0, [bg, this.card]).setDepth(depth).setVisible(false);

    if (!scene.textures.exists(CONFETTI_KEY)) {
      const t = scene.textures.createCanvas(CONFETTI_KEY, 6, 10);
      if (t) {
        const ctx = t.getContext();
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, 6, 10);
        t.refresh();
      }
    }
    // it rains down from the top edge over the whole window, slowly turning and drifting a little (like on the phone)
    const s = area.height / 400;
    const fall = height * 1.1;
    const [fast, slow] = CONFETTI_FALL_SEC;
    this.confetti = scene.add
      .particles(0, 0, CONFETTI_KEY, {
        emitting: false,
        x: { min: 0, max: width },
        y: -12 * s,
        speedY: { min: fall / slow, max: fall / fast },
        speedX: { min: (-0.08 * fall) / slow, max: (0.08 * fall) / slow },
        lifespan: slow * 1000,
        rotate: {
          onEmit: (p) => {
            const spin = { from: Math.random() * 360, turn: (Math.random() < 0.5 ? -1 : 1) * Phaser.Math.Between(540, 720) };
            if (p) this.spins.set(p, spin);
            return spin.from;
          },
          onUpdate: (p, _key, t, value) => {
            const spin = this.spins.get(p);
            return spin ? spin.from + spin.turn * t : value;
          },
        },
        scale: { min: 0.9 * s, max: 1.4 * s },
        tint: CONFETTI_COLORS,
      })
      .setDepth(depth + 1);
  }

  get step(): EndingStep | null {
    return this._step;
  }

  /** the end screen covers the window */
  get shown(): boolean {
    return this.root.visible;
  }

  start(content: EndingContent, score: Score): void {
    this.content = content;
    this.score = score;
    this.sec = 0;
    this.counted = -1;
    this.root.setVisible(true).setAlpha(0);
    this.scene.tweens.add({ targets: this.root, alpha: 1, duration: 300 });
    this.show('result');
  }

  update(dt: number): void {
    const c = this.content;
    if (!c || !this._step) return;
    this.sec += dt;
    if (this._step === 'result') {
      this.countUp();
      if (this.sec >= c.storyAtSec) this.show(c.story.length ? 'story' : 'cta');
    } else if (this._step === 'story') {
      if (this.sec >= c.ctaAtSec) this.show('cta');
      else if (this.storyPart() !== this.part) this.show('story');
    }
  }

  /** seconds per part of the story */
  private get partSec(): number {
    const c = this.content!;
    return (c.ctaAtSec - c.storyAtSec) / Math.max(1, c.story.length);
  }

  private storyPart(): number {
    const c = this.content!;
    return Math.min(c.story.length - 1, Math.max(0, Math.floor((this.sec - c.storyAtSec) / this.partSec)));
  }

  /** straight to the next screen (a tap on the phone) */
  skip(): void {
    const c = this.content;
    if (!c || !this._step) return;
    if (this._step === 'result') this.sec = Math.max(this.sec, c.storyAtSec);
    else if (this._step === 'story') this.sec = Math.max(this.sec, c.storyAtSec + (this.part + 1) * this.partSec);
    this.update(0);
  }

  hide(): void {
    this.content = null;
    this._step = null;
    this.scene.tweens.killTweensOf([this.root, this.card]);
    this.root.setVisible(false);
  }

  private show(step: EndingStep): void {
    const c = this.content!;
    const score = this.score!;
    this._step = step;
    for (const t of [this.title, this.depotLabel, this.depot, this.subsidyLabel, this.subsidy, this.text]) t.setText('');
    if (step === 'result') {
      const t = c.result(score);
      this.title.setText(t.title).setColor(score.max ? CSS.gold : CSS.orange);
      this.text.setText(t.text);
      // laid out with the final amounts, then counted up to them
      this.counted = 1;
      this.depotLabel.setText(c.amounts.own);
      this.subsidyLabel.setText(`+ ${c.amounts.subsidy}`);
      this.setAmounts(score.ownEur, score.subsidyEur);
      // confetti with the result (more for the maximum)
      this.burst(score.max ? 3 : 1.5);
    } else if (step === 'story') {
      this.part = this.storyPart();
      this.title.setText(c.story[this.part] ?? '').setColor(CSS.text);
    } else {
      this.title.setText(c.cta.headline).setColor(c.cta.color ?? CSS.orange);
      this.text.setText(c.cta.hint ?? '');
    }
    this.layout();
    if (step === 'result') {
      this.counted = -1;
      this.countUp();
    }
    this.card.setAlpha(0).setScale(this.card.scaleX * 0.9);
    this.scene.tweens.add({ targets: this.card, alpha: 1, scale: this.card.scaleX / 0.9, duration: 320, ease: 'Back.Out' });
    this.onStep(step);
  }

  /** own contribution and subsidy count up from 0 */
  private countUp(): void {
    const s = this.score!;
    const p = Phaser.Math.Easing.Cubic.Out(Math.min(1, this.sec / COUNT_SEC));
    if (p === this.counted) return;
    this.counted = p;
    // whole euros while counting, the exact amount at the end
    const own = p < 1 ? Math.round(s.ownEur * p) : s.ownEur;
    const sub = p < 1 ? Math.round(s.subsidyEur * p) : s.subsidyEur;
    this.setAmounts(own, sub);
  }

  private setAmounts(own: number, sub: number): void {
    this.depot.setText(formatEuro(own));
    this.subsidy.setText(formatEuro(sub));
  }

  /** stack the texts of the screen in the middle of the window, scaled down if they do not fit */
  private layout(): void {
    const px = (u: number) => u * this.unit;
    const parts = [this.title, this.depotLabel, this.depot, this.subsidyLabel, this.subsidy, this.text].filter((t) => t.text);
    // the space below each text: a label sits close to its amount
    const gap = (t: Phaser.GameObjects.Text) => (t === this.depotLabel || t === this.subsidyLabel ? px(0) : px(TYPE.text * 0.5));
    let y = 0;
    parts.forEach((t, i) => {
      t.setPosition(0, y);
      y += t.height + (i < parts.length - 1 ? gap(t) : 0);
    });
    for (const t of parts) t.y -= y / 2;
    const a = this.area;
    const top = (this.content?.topSpace ?? 0) * a.height;
    const h = a.height - top - (this.content?.bottomSpace ?? 0) * a.height;
    const w = Math.max(...parts.map((t) => t.width));
    const scale = Math.min(1, (a.width * 0.94) / w, (h * 0.9) / y);
    this.card.setPosition(a.x + a.width / 2, a.y + top + h / 2).setScale(scale);
  }

  /** confetti raining down from the top edge of the window */
  private burst(strength: number): void {
    const batches = 6;
    const n = Math.round((60 * strength) / batches);
    for (let i = 0; i < batches; i++) {
      this.scene.time.delayedCall((i * CONFETTI_SPREAD_SEC * 1000) / (batches - 1), () => this.confetti.explode(n));
    }
  }
}
